/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * List Page State Hook
 *
 * Composes useStandardFilters + useDomainSearch to provide a unified interface
 * for list pages with search, filtering, and pagination state management.
 * Reduces duplication across list pages by centralizing common patterns.
 */

"use client";

import { type RefObject } from "react";

import { useDomainSearch } from "../data/use-domain-search";
import {
  useStandardFilters,
  type StandardFiltersConfig,
  type StandardFiltersReturn,
} from "../filters/use-standard-filters";

type FilterRecord = Record<string, unknown>;

/**
 * Configuration for the list page state hook
 */
export interface UseListPageStateConfig<
  TFilter extends FilterRecord = FilterRecord,
> extends Omit<StandardFiltersConfig<TFilter>, "id"> {
  /**
   * Stable identifier for the filter surface (e.g., "services" or "projects").
   * Used to derive deterministic URL parameter keys.
   */
  id: string;

  /**
   * Placeholder text for the search input
   * @example "Search projects..."
   */
  searchPlaceholder: string;

  /**
   * Whether user has permission to create new items
   * Used for conditional keyboard shortcuts
   */
  canCreate?: boolean;

  /**
   * Handler for creating new items (triggered by Cmd+N/N shortcuts)
   */
  onCreateNew?: () => void;

  /**
   * Additional keyboard shortcuts specific to the domain
   */
  additionalShortcuts?: Array<{
    keys: string;
    action: () => void;
    preventDefault?: boolean;
  }>;

  /**
   * Optional test ID prefix for the search input
   * Will be used as `${testIdPrefix}-search-input`
   */
  testIdPrefix?: string;
}

/**
 * Return type for useListPageState hook
 */
export interface UseListPageStateReturn<
  TFilter extends FilterRecord = FilterRecord,
> extends Omit<StandardFiltersReturn<TFilter>, "getPaginationInfo"> {
  /**
   * Ref for the search input (for focusing with keyboard shortcuts)
   */
  searchInputRef: RefObject<HTMLInputElement | null>;

  /**
   * Props to spread onto StandardSearch component
   */
  searchProps: {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    onClear?: () => void;
    testId?: string;
  };

  /**
   * Helper to focus the search input
   */
  focusSearch: () => void;

  /**
   * Setter for search term input (exposed for compatibility)
   */
  setSearchTermInput: (value: string) => void;
}

/**
 * List Page State Hook
 *
 * Composes useStandardFilters and useDomainSearch to provide a unified interface
 * for list pages. This hook reduces duplication by centralizing the common pattern
 * of combining search, filtering, and pagination state management.
 *
 * Features:
 * - Unified search, filter, and pagination state
 * - Automatic keyboard shortcuts (mod+f for search, mod+n/n for create)
 * - URL synchronization support (via useStandardFilters)
 * - Type-safe filter state management
 * - StandardSearch-compatible props
 *
 * @example
 * ```typescript
 * // Basic usage
 * const listState = useListPageState({
 *   id: "projects",
 *   initialFilters: { status: "all", ownerId: "all" },
 *   pageSize: 20,
 *   syncWithUrl: false,
 *   searchPlaceholder: "Search projects...",
 *   canCreate: canManageData,
 *   onCreateNew: () => router.push("/app/projects/new"),
 *   testIdPrefix: "projects",
 * });
 *
 * // Use in component
 * <UnifiedFilterBar
 *   search={{
 *     searchInputRef: listState.searchInputRef,
 *     searchProps: listState.searchProps,
 *   }}
 * />
 * ```
 */
export function useListPageState<TFilter extends FilterRecord = FilterRecord>(
  config: UseListPageStateConfig<TFilter>,
): UseListPageStateReturn<TFilter> {
  const {
    id,
    initialFilters,
    pageSize,
    searchDebounceMs,
    syncWithUrl,
    urlId,
    urlKeys,
    filterUrlKeys,
    searchPlaceholder,
    canCreate,
    onCreateNew,
    additionalShortcuts,
    testIdPrefix,
  } = config;

  // Use standard filters for search, pagination, and filter state
  const standardFilters = useStandardFilters<TFilter>({
    id,
    initialFilters,
    pageSize,
    searchDebounceMs,
    ...(syncWithUrl
      ? {
          syncWithUrl: true as const,
          urlId,
          urlKeys,
          filterUrlKeys,
        }
      : {
          syncWithUrl: false as const,
        }),
  });

  // Use domain search for search input ref and props
  const domainSearch = useDomainSearch({
    placeholder: searchPlaceholder,
    searchTerm: standardFilters.searchTermInput,
    onSearchChange: standardFilters.setSearchTermInput,
    onClear: standardFilters.handleClearSearch,
    canCreate,
    onCreateNew,
    additionalShortcuts,
    testIdPrefix: testIdPrefix ?? id,
  });

  // Return composed interface
  return {
    // Search state (from useStandardFilters)
    searchTermInput: standardFilters.searchTermInput,
    setSearchTermInput: standardFilters.setSearchTermInput,
    searchTermServer: standardFilters.searchTermServer,

    // Pagination state (from useStandardFilters)
    page: standardFilters.page,
    setPage: standardFilters.setPage,
    pageSize: standardFilters.pageSize,
    setPageSize: standardFilters.setPageSize,

    // Filter state (from useStandardFilters)
    filterState: standardFilters.filterState,
    setFilterState: standardFilters.setFilterState,

    // Actions (from useStandardFilters)
    handleClearAllFilters: standardFilters.handleClearAllFilters,
    handleClearSearch: standardFilters.handleClearSearch,

    // Search UI (from useDomainSearch)
    searchInputRef: domainSearch.searchInputRef,
    searchProps: domainSearch.searchProps,
    focusSearch: domainSearch.focusSearch,
  };
}
